import { gql } from "@apollo/client"
import type { LicensesSet, Role } from "../../../mp-database"
import {
  FeatureFlagName
} from "../../../mp-utils/generic-utils/feature-flags/types"
import type { FeatureFlagStatesType } from "../../../mp-utils/generic-utils/feature-flags/types"
import { getLicenseStatus } from "../../../mp-utils/ready-utils/hooks"
import { executeQuerySideload } from "../../../../services/generic-services/helpers/studyPlan"
import apiClient from "../../api/apiClient"
import type { Column } from "../../components/Table"
import {
  DEFAULT_DISPLAYED_PROPERTIES,
  FILTERS_BY_ROUTE,
  HEADER_COLUMNS,
  REQUEST_PARAMS
} from "../../constants"
import cubeClient from "../../cubeClient"
import {
  FIELDS_WITH_FEATURE_FLAGS_DEPS,
  FILTERS_BY_ROUTE_UNDER_FEATURE_FLAGS,
  WIDGETS_UNDER_FEATURE_FLAGS
} from "./constants"
import { getRedirectPathByNavItem, sortGoalsData } from "../../helpers"
import type { GetUserGoalsResponse, NavItem, UserGoalsData } from "../../types"
import type { LicenseNavCategories } from "../../../../pages/Router"

const LICENSE_NAV_CATEGORIES = {
  MasteryResources: "Mastery Resources" as LicenseNavCategories,
  ACTResources: "ACT Resources" as LicenseNavCategories
}

const PROGRAMS_WITH_OVERALL_SCORE_RANGE_QUERY = gql`
  query ProgramsWithOverallScoreRange {
    reporting {
      programsWithOverallScoreRange
    }
  }
`

export const getProgramsWithOverallScoreRange = async (
  useGraphQL = false
): Promise<string[]> => {
  if (useGraphQL) {
    const response = await executeQuerySideload(
      PROGRAMS_WITH_OVERALL_SCORE_RANGE_QUERY,
      {}
    )

    return response?.reporting?.programsWithOverallScoreRange || []
  }

  const response: Record<string, string>[] = await cubeClient.get("/", {
    params: {
      query: JSON.stringify({
        dimensions: ["test_view_score_ranges.program"],
        filters: [
          {
            values: ["OVERALL"],
            member: "test_view_score_ranges.metricType",
            operator: "equals"
          }
        ]
      })
    }
  })

  return response?.map(item => item["test_view_score_ranges.program"]) || []
}

export const getAvailableGoals = async (): Promise<UserGoalsData[]> => {
  const response = await apiClient
    .get<GetUserGoalsResponse, GetUserGoalsResponse>("/goals")
    .then(response => sortGoalsData(response.data || []))

  return response
}

export const getHiddenFieldsByFeatureFlag = (
  featureFlags: Record<string, boolean>
): string[] => {
  return Object.entries(FIELDS_WITH_FEATURE_FLAGS_DEPS).reduce(
    (res, [flagName, fields]) => {
      if (!!featureFlags[flagName]) {
        return res
      }

      return [...res, ...fields]
    },
    []
  )
}

export const getFilteredHeaderColumns = (
  excludedFields: string[]
): Record<string, Record<string, Column[]>> => {
  if (!excludedFields?.length) {
    return HEADER_COLUMNS
  }

  return Object.fromEntries(
    Object.entries(HEADER_COLUMNS).map(([page, pageData]) => {
      return [
        page,
        Object.fromEntries(
          Object.entries(pageData).map(([subpage, subpageData]) => {
            return [
              subpage,
              subpageData.filter(item => !excludedFields.includes(item.name))
            ]
          })
        )
      ]
    })
  )
}

export const getFilteredDisplayedProperties = (excludedFields: string[]) => {
  if (!excludedFields?.length) {
    return DEFAULT_DISPLAYED_PROPERTIES
  }

  return Object.fromEntries(
    Object.entries(DEFAULT_DISPLAYED_PROPERTIES).map(([page, pageData]) => {
      return [
        page,
        Object.fromEntries(
          Object.entries(pageData).map(([subpage, subpageData]) => {
            return [
              subpage,
              subpageData.filter(item => !excludedFields.includes(item))
            ]
          })
        )
      ]
    })
  )
}

export const getFilteredRequestParams = (excludedFields: string[]) => {
  if (!excludedFields?.length) {
    return REQUEST_PARAMS
  }

  return Object.fromEntries(
    Object.entries(REQUEST_PARAMS).map(([page, pageData]) => {
      return [
        page,
        Object.fromEntries(
          Object.entries(pageData).map(([subpage, subpageData]) => {
            return [
              subpage,
              Object.fromEntries(
                Object.entries(subpageData).map(
                  ([cubeRequestItem, cubeRequestItemData]: [
                    string,
                    string[]
                  ]) => {
                    return [
                      cubeRequestItem,
                      cubeRequestItemData?.filter(
                        item => !excludedFields.includes(item)
                      )
                    ]
                  }
                )
              )
            ]
          })
        )
      ]
    })
  )
}

export const getExcludedWidgetsByFeatureFlags = (
  featureFlags: Record<string, boolean>
) => {
  return Object.entries(WIDGETS_UNDER_FEATURE_FLAGS).reduce(
    (res, [flagName, widgets]) => {
      if (!!featureFlags[flagName]) {
        return res
      }

      return [...res, ...widgets]
    },
    []
  )
}

export const getAvailableNavItemsUnderFeatureFlagAndLicense = (
  navItem: NavItem,
  featureFlags: FeatureFlagStatesType,
  userLicenses: LicensesSet[],
  isACTDistrict: boolean = false,
  userRole?: string
): NavItem | null => {
  if (navItem.featureFlag && !featureFlags[navItem.featureFlag]) {
    return null
  }

  // Hide Content Mastery menu if feature flag is enabled and user has only EACT licenses
  if (
    navItem.label === "Content Mastery" &&
    !!featureFlags?.[
      FeatureFlagName.reporting_insights_performance_content_mastery_hide_eact
    ]
  ) {
    const { hasOnlyEACTLicenses } = getLicenseStatus(userLicenses)
    if (hasOnlyEACTLicenses) {
      return null
    }
  }

  if (
    navItem.href === "/reporting/organization/organizations" &&
    userRole !== "Super Administrator" &&
    userRole !== "ACT Admin"
  ) {
    return null
  }
  // Check if this is a license management nav item that requires ACT district
  if (
    navItem.href?.includes("license-pools") ||
    navItem.href?.includes("license-assign")
  ) {
    // Special admin roles always see license management
    const specialAdminRoles = [
      "Super Administrator",
      "ACT Admin",
      "ACT Multi-District Admin"
    ]

    if (userRole && specialAdminRoles.includes(userRole)) {
      // Continue - don't filter out
    } else {
      // For District Admin and School Admin, check ACT district flag
      if (!isACTDistrict) {
        return null
      }
    }
  }

  // License check - specifically enforce for Skill Review and Mastery Curriculum
  if (navItem.navCategory && navItem.navCategory.length > 0) {
    // Check if this is Skill Review
    const isSkillReview =
      navItem.label === "Skill Review" ||
      navItem.href?.includes("/skill-review")

    // Check if this is Mastery Curriculum/Books
    const isMasteryCurriculum =
      navItem.label === "Books" || navItem.href?.includes("/mastery-curriculum")

    // For Skill Review, only allow EACT licenses
    if (isSkillReview) {
      const hasValidLicense =
        userLicenses &&
        userLicenses.length > 0 &&
        userLicenses.some(l => {
          // Extract program name from first word of navName
          const programName = l.navName?.split(" ")[0]?.toLowerCase()
          // Must be a Study Hall license AND specifically for EACT
          return (
            navItem.navCategory.includes(
              l.navCategory as LicenseNavCategories
            ) &&
            !l.isExpired &&
            programName === "eact"
          )
        })

      if (!hasValidLicense) {
        return null // Hide navigation item
      }
    }
    // For Mastery Curriculum, only allow DSAT or EACT licenses
    else if (isMasteryCurriculum) {
      const hasValidLicense =
        userLicenses &&
        userLicenses.length > 0 &&
        userLicenses.some(l => {
          // Extract program name from first word of navName
          let programName = l.navName?.split(" ")[0]?.toLowerCase()
          if (programName !== "act" && programName !== "eact") {
            if (l.navName.includes("Digital SAT")) {
              programName = "dsat"
            }
          }

          // For DSAT and EACT: must be Mastery Resources
          // For ACT: must be ACT Resources
          if (
            programName === "dsat" ||
            programName === "eact" ||
            programName === "tsia2"
          ) {
            return (
              l.navCategory === LICENSE_NAV_CATEGORIES.MasteryResources &&
              !l.isExpired
            )
          } else if (programName === "act") {
            return (
              l.navCategory === LICENSE_NAV_CATEGORIES.ACTResources &&
              !l.isExpired
            )
          }

          return false
        })

      if (!hasValidLicense) {
        return null // Hide navigation item
      }
    }
    // For other items, use the existing logic
    else {
      const validLicense = userLicenses.some(l => {
        return (
          navItem.navCategory.includes(l.navCategory as LicenseNavCategories) &&
          !l.isExpired
        )
      })
      if (!validLicense) return null
    }
  }

  const { children, ...rest } = navItem
  const filteredItem: NavItem = { ...rest }

  if (navItem.children?.length) {
    const filteredChildren = navItem.children
      .map(item =>
        getAvailableNavItemsUnderFeatureFlagAndLicense(
          item,
          featureFlags,
          userLicenses,
          isACTDistrict,
          userRole
        )
      )
      .filter(item => item !== null)

    if (filteredChildren.length > 0) {
      filteredItem.children = filteredChildren
    }
  }

  return filteredItem
}

export const getRedirectPath =
  (navItem: NavItem, userRole: Role, targetPath?: string) => (path: string) => {
    if (!navItem || !path) return targetPath ?? "/reporting"

    return (
      getRedirectPathByNavItem(navItem, path, userRole) ||
      (targetPath ?? "/reporting")
    )
  }

export const getFiltersByRouteUnderFeatureFlag = (
  featureFlags: FeatureFlagStatesType
) => {
  return Object.entries(FILTERS_BY_ROUTE_UNDER_FEATURE_FLAGS).reduce(
    (res, [flag, routesFilters]) => {
      const featureFlagState = featureFlags[flag as FeatureFlagName]
      if (!!featureFlagState) {
        return res
      }

      const updatedRoutesFilters = Object.fromEntries(
        Object.entries(routesFilters).map(([route, excludedFilters]) => [
          route,
          [...res[route].filter(filter => !excludedFilters.includes(filter))]
        ])
      )

      return {
        ...res,
        ...updatedRoutesFilters
      }
    },
    { ...FILTERS_BY_ROUTE }
  )
}
